// Single place that knows how to talk to the backend. Every page goes through
// this; no component builds a URL or reads the token itself.

// In production the API is served under /api on the same host, so the base is
// an empty string and requests are same-origin.
//
// The default keys off NODE_ENV rather than relying on REACT_APP_API_URL being
// set to an empty string at build time: an empty shell variable does not
// survive the npm.cmd shim on Windows, and the build silently baked
// "http://localhost:8000" into the deployed bundle. `??` (not `||`) so an
// explicit empty override still means same-origin.
const DEFAULT_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';
const BASE_URL = process.env.REACT_APP_API_URL ?? DEFAULT_BASE;

const TOKEN_KEY = 'fitlife_token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

async function request(path, { method = 'GET', body, timeout = 60000,
                                keepalive = false } = {}) {
  const token = getToken();
  const controller = new AbortController();
  // `keepalive` is for a request fired as the page goes away — an undo window
  // that expired at the same moment the tab closed. An AbortController tied to
  // a timer would be torn down with the document, so it is left off.
  const timer = keepalive ? null : setTimeout(() => controller.abort(), timeout);

  let res;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method,
      ...(keepalive ? { keepalive: true } : { signal: controller.signal }),
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch (err) {
    if (timer) clearTimeout(timer);
    if (err.name === 'AbortError') {
      throw new ApiError('The request took too long. Try again.', 0);
    }
    throw new ApiError("Can't reach the server. Is the backend running?", 0);
  }
  if (timer) clearTimeout(timer);

  if (res.status === 204) return null;

  const payload = await res.json().catch(() => null);

  if (!res.ok) {
    // FastAPI validation errors arrive as a list of {loc, msg}.
    let detail = payload?.detail;
    if (Array.isArray(detail)) {
      detail = detail.map((d) => `${d.loc?.slice(-1)[0] ?? ''} ${d.msg}`.trim()).join('; ');
    }
    throw new ApiError(detail || `Request failed (${res.status})`, res.status);
  }
  return payload;
}

const qs = (params) => {
  const entries = Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return entries.length ? `?${new URLSearchParams(entries)}` : '';
};

export const api = {
  auth: {
    register: (d) => request('/api/auth/register', { method: 'POST', body: d }),
    login: (d) => request('/api/auth/login', { method: 'POST', body: d }),
    me: () => request('/api/auth/me'),
  },
  profile: {
    update: (d) => request('/api/profile/', { method: 'PUT', body: d }),
    stats: () => request('/api/profile/stats'),
    getPlan: () => request('/api/profile/active-plan'),
    setPlan: (d) => request('/api/profile/active-plan', { method: 'PUT', body: d }),
  },
  food: {
    list: (date) => request(`/api/logs/food${qs({ date_filter: date })}`),
    create: (d) => request('/api/logs/food', { method: 'POST', body: d }),
    remove: (id, opts) => request(`/api/logs/food/${id}`, { method: 'DELETE', ...opts }),
  },
  exercise: {
    list: (date) => request(`/api/logs/exercise${qs({ date_filter: date })}`),
    create: (d) => request('/api/logs/exercise', { method: 'POST', body: d }),
    remove: (id, opts) => request(`/api/logs/exercise/${id}`, { method: 'DELETE', ...opts }),
  },
  weight: {
    list: (limit = 100) => request(`/api/logs/weight${qs({ limit })}`),
    create: (d) => request('/api/logs/weight', { method: 'POST', body: d }),
    remove: (id, opts) => request(`/api/logs/weight/${id}`, { method: 'DELETE', ...opts }),
  },
  conditions: {
    list: () => request('/api/logs/comorbidities'),
    create: (d) => request('/api/logs/comorbidities', { method: 'POST', body: d }),
    remove: (id, opts) => request(`/api/logs/comorbidities/${id}`, { method: 'DELETE', ...opts }),
  },
  bloodwork: {
    list: () => request('/api/bloodwork'),
    modelInfo: () => request('/api/bloodwork/model', { timeout: 60000 }),
    trends: () => request('/api/bloodwork/trends', { timeout: 60000 }),
    submit: (d) => request('/api/bloodwork', { method: 'POST', body: d, timeout: 120000 }),
    analyse: (id) => request(`/api/bloodwork/${id}/analyze`, { timeout: 120000 }),
    remove: (id, opts) => request(`/api/bloodwork/${id}`, { method: 'DELETE', ...opts }),
  },
  workout: {
    recommend: (windowDays = 28, perGroup = 3) =>
      request(`/api/workout/recommend${qs({ window_days: windowDays, per_group: perGroup })}`,
        { timeout: 60000 }),
  },
  weightAI: {
    predict: (days = 30) => request(`/api/weight/predict${qs({ days })}`, { timeout: 120000 }),
  },
  dietAI: {
    macro: (d) => request('/api/recommend/macro', { method: 'POST', body: d, timeout: 180000 }),
    discover: (d) => request('/api/recommend/discover', { method: 'POST', body: d, timeout: 180000 }),
    collab: (d) => request('/api/recommend/collab', { method: 'POST', body: d, timeout: 180000 }),
  },
  chat: {
    ask: (message, includeHealthData = true) =>
      request('/api/chat/llama', {
        method: 'POST',
        body: { message, include_health_data: includeHealthData },
        timeout: 300000,
      }),
  },
  videos: {
    suggestions: () => request('/api/youtube/suggestions', { timeout: 60000 }),
    search: (q, maxResults = 9) => request(`/api/youtube/search${qs({ q, max_results: maxResults })}`, { timeout: 60000 }),
  },
};

export { ApiError };
